import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineUserGroup, HiOutlineAcademicCap, HiOutlineStar } from "react-icons/hi";
import { quizLeaderboard } from "./quizApiService";
import { getTeacherSession } from "./quizSession";

const MEDALS = ["🥇", "🥈", "🥉"];

function formatTime(ms) {
  if (!ms || ms < 0) return "—";
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function buildLeaderboard(rawParticipants) {
  return rawParticipants
    .map((p) => {
      const answers = p.answers || [];
      const correct = answers.filter((a) => a.status === "CORRECT").length;
      const wrong = answers.filter((a) => a.status && a.status !== "CORRECT").length;
      const totalTimeMs = answers.reduce((sum, a) => sum + (a.timeTakenMs || 0), 0);
      return {
        id: p.uuidParticipant || p.uuidParticipan || p.name,
        name: p.name || "—",
        score: correct,
        correct,
        wrong,
        totalTimeMs,
      };
    })
    .sort((a, b) => b.score - a.score || a.totalTimeMs - b.totalTimeMs);
}

export default function QuizTeacherResults() {
  const { t } = useTranslation();
  const { sessionId } = useParams(); // shortId
  const navigate = useNavigate();

  const session = useRef(getTeacherSession(sessionId)).current;
  const totalQuestions = session?.cards?.length || 0;

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session?.uuidQuiz) {
      setLoading(false);
      setError(t("quiz.sessionNotFound") || "Quiz session not found");
      return;
    }
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const raw = await quizLeaderboard({ uuidQuiz: session.uuidQuiz });
        setLeaderboard(buildLeaderboard(raw));
      } catch (err) {
        console.error("Failed to load leaderboard:", err);
        setError(err?.response?.data?.data?.error || err?.message || "Failed to load results");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [session, t]);

  const totalStudents = leaderboard.length;
  const averageScore = totalStudents
    ? (leaderboard.reduce((sum, s) => sum + s.score, 0) / totalStudents).toFixed(1)
    : "0";
  const bestScore = totalStudents ? Math.max(...leaderboard.map((s) => s.score)) : 0;

  return (
    <div className="flex-1 w-full">
      <div className="max-w-[1000px] mx-auto p-5">
        {/* Header */}
        <div className="mb-8 text-center pt-4">
          <h1 className="text-3xl font-bold text-black mb-2">{t("quiz.quizResults")}</h1>
          <p className="text-sm font-medium text-darkblue bg-blue-50 inline-block px-3 py-1 rounded-full">
            {t("quiz.joinCode") || "Join code"}: {sessionId}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-[3px] border-darkblue border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-400">{t("loading") || "Loading..."}</span>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-lg text-red-500 mb-4">{error}</p>
            <button
              onClick={() => navigate("/quiz")}
              className="primary-btn !rounded-full h-11 !py-0 flex items-center justify-center px-7 mx-auto"
            >
              {t("quiz.backToHome")}
            </button>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-3 md:grid-cols-1 gap-5 mb-8">
              <div className="rounded-3xl border border-blue-50 shadow-sm p-6 bg-white text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <HiOutlineUserGroup className="text-2xl text-darkblue" />
                </div>
                <p className="text-3xl font-bold text-black">{totalStudents}</p>
                <p className="text-sm font-medium text-lightgray mt-1">{t("quiz.totalStudents")}</p>
              </div>
              <div className="rounded-3xl border border-blue-50 shadow-sm p-6 bg-white text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <HiOutlineAcademicCap className="text-2xl text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-black">
                  {averageScore}
                  <span className="text-lg text-lightgray font-medium">/{totalQuestions}</span>
                </p>
                <p className="text-sm font-medium text-lightgray mt-1">{t("quiz.averageScore")}</p>
              </div>
              <div className="rounded-3xl border border-blue-50 shadow-sm p-6 bg-white text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <HiOutlineStar className="text-2xl text-yellow-600" />
                </div>
                <p className="text-3xl font-bold text-black">
                  {bestScore}
                  <span className="text-lg text-lightgray font-medium">/{totalQuestions}</span>
                </p>
                <p className="text-sm font-medium text-lightgray mt-1">{t("quiz.bestScore")}</p>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="rounded-3xl bg-white p-6 shadow-sm border border-blue-50 mb-8">
              <h2 className="text-lg font-bold text-black mb-5 flex items-center gap-2">
                {t("quiz.leaderboard")}
              </h2>

              {leaderboard.length === 0 ? (
                <p className="text-center text-lightgray py-8">
                  {t("quiz.noParticipants") || "No participants"}
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-[48px_1fr_80px_60px_60px_60px] gap-2 px-4 py-3 text-xs font-bold text-lightgray uppercase tracking-wider border-b border-blue-50 mb-2">
                    <span>#</span>
                    <span>{t("quiz.name") || "Name"}</span>
                    <span className="text-center">{t("quiz.score") || "Score"}</span>
                    <span className="text-center text-green-600 text-sm">✓</span>
                    <span className="text-center text-red-500 text-sm">✗</span>
                    <span className="text-center">{t("quiz.time") || "Time"}</span>
                  </div>

                  <div className="space-y-2">
                    {leaderboard.map((student, index) => {
                      const rank = index + 1;
                      let rowBg = "bg-white border border-transparent";
                      if (rank === 1) rowBg = "bg-yellow-50/50 border border-yellow-100";
                      else if (rank === 2) rowBg = "bg-gray-50 border border-gray-200";
                      else if (rank === 3) rowBg = "bg-orange-50/50 border border-orange-100";
                      else if (index % 2 === 1) rowBg = "bg-slate-50/50 border border-transparent";

                      return (
                        <div
                          key={student.id}
                          className={`grid grid-cols-[48px_1fr_80px_60px_60px_60px] gap-2 px-4 py-3 items-center rounded-2xl transition-colors hover:shadow-sm ${rowBg}`}
                        >
                          <span className="text-xl font-bold">
                            {rank <= 3 ? MEDALS[rank - 1] : <span className="text-base text-gray-400">#{rank}</span>}
                          </span>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-darkblue text-white flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-sm">
                              {student.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-black truncate">{student.name}</span>
                          </div>
                          <span className="font-bold text-darkblue text-center bg-blue-50 py-1 rounded-lg">
                            {student.score}/{totalQuestions}
                          </span>
                          <span className="font-bold text-green-600 text-center">{student.correct}</span>
                          <span className="font-bold text-red-500 text-center">{student.wrong}</span>
                          <span className="text-xs font-medium text-lightgray text-center bg-white px-2 py-1 rounded-md border border-gray-100">
                            {formatTime(student.totalTimeMs)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => navigate("/quiz")}
                className="secondary-btn flex-1 !rounded-full h-12 !py-0 flex items-center justify-center text-base font-semibold"
              >
                {t("quiz.playAgain")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
